/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    Box,
    Container,
    Heading,
    Skeleton as ChakraSkeleton,
    Stack,
    Text,
    Button,
    useBreakpointValue,
    useDisclosure,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {EditIcon} from '../../components/forms/FormIcons'
import {LeftArrow} from '../../components/icons'
import {
    ToggleCard,
    ToggleCardEdit,
    ToggleCardSummary
} from '@salesforce/retail-react-app/app/components/toggle-card'
import ProfileFields from '../../components/forms/profile-fields'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAccountEdit} from '../../contexts/account-edit-context'
import CloseAccountModal from '../../components/close-account-modal'
import { useCustomToast } from '../../contexts/custom-toast-context'
import usedDeviceDetection from '../../hooks/use-device-detection'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useLocalePhonePrefix from '../../hooks/use-locale-phone-prefix'
import {getDefaultPhoneInputValue} from '../../utils/phone-utils'

const DEFAULT_CUSTOMER_NAME = 'Customer'

/**
 * This is a specialized Skeleton component that which uses the customers authtype as the
 * `isLoaded` state. It also will revert it's provided size (height, width) when the loaded
 * state changes. This allows you to have skeletons of a specific size, but onece loaded
 * the bounding element will affect the contents size.
 */
// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})

Skeleton.displayName = 'Skeleton'

const ProfileCard = () => {
    const intl = useIntl()
    const theme = useTheme()
    const { baseStyle: toastStyling } = theme.components.CustomToast
    const isMobile = useBreakpointValue({ base: true, md: false })
    const { showCustomToast } = useCustomToast()
    const headingRef = useRef(null)
    const {data: customer} = useCurrentCustomer()
    const {isRegistered, customerId} = customer
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()
    const {
        isOpen: isOpenCloseAccountModal,
        onOpen: onOpenCloseAccountModal,
        onClose: onCloseCloseAccountModal
    } = useDisclosure()

    const device = usedDeviceDetection()
    const {locale} = useMultiSite()
    const phonePrefix = useLocalePhonePrefix()

    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')

    const [isEditing, setIsEditing] = useState(false)
    const {startEditing, stopEditing, setFormDirty} = useAccountEdit();

    const form = useForm({
        defaultValues: {
            firstName: customer?.firstName,
            lastName: customer?.lastName !== DEFAULT_CUSTOMER_NAME ? customer?.lastName : '',
            email: customer?.email,
            phone: customer?.phoneHome,
            loyaltyId: customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
            birthDay: customer?.c_birthdayFormatted
        }
    })

    useEffect(() => {
        form.reset({
            firstName: customer.firstName,
            lastName: customer?.lastName !== DEFAULT_CUSTOMER_NAME ? customer?.lastName : '',
            email: customer.email,
            phone: customer.phoneHome,
            loyaltyId: customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
            birthDay: customer?.c_birthdayFormatted
        })
    }, [
        customer?.firstName,
        customer?.lastName,
        customer?.email,
        customer?.phoneHome,
        customer?.c_b2cloyalty_loyaltyProgramMemberNumber,
        customer?.c_birthdayFormatted
    ])

    useEffect(() => {
        if (isEditing) {
            startEditing()
        } else {
            stopEditing()
        }
    }, [isEditing])

    useEffect(() => {
        if (isEditing) {
            setFormDirty(form.formState.isDirty)
        }
    }, [isEditing, form.formState.isDirty])

    const submit = async (values) => {
        try {
            form.clearErrors()

            // Build body conditionally
            const body = {
                firstName: values.firstName,
                lastName: values.lastName,
                phoneHome: values.phone,
                email: values.email,
                login: values.email,
                c_device: device,
                c_locale: locale?.id
            }

            // Only include birthday if it's a proper ISO string (from selector)
            const isValidDateISO = /^\d{4}-\d{2}-\d{2}$/.test(values.birthDay)
            if (isValidDateISO) {
                body.birthday = values.birthDay
            }

            updateCustomerMutation.mutate(
                {
                    parameters: {customerId, c_brand: 'Garage'},
                    body
                },
                {
                    onSuccess: () => {
                        setIsEditing(false)
                        showCustomToast({
                            message: intl.formatMessage({
                                defaultMessage: 'The changes have been saved',
                                id: 'account.address_book.success_message'
                            }),
                            styles: { ...toastStyling, position: {base: 'relative', lg: 'static'}},
                            containerId: isMobile ? 'app-header' : 'account-left-container',
                        })
                        headingRef?.current?.focus()
                    }
                }
            )
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
            // GTM Modification
            sendViewErrorEvent(error.message)
        }
    }

    const handleStartEditing = () => {
        setIsEditing(true)

        const currentPhone = form.getValues('phone')
        if (!currentPhone?.trim()) {
            form.setValue('phone', getDefaultPhoneInputValue(phonePrefix), {
                shouldDirty: false
            })
        }
    }

    return (
        <>
            <ToggleCard
                id="my-profile"
                editing={isEditing}
                isLoading={form.formState.isSubmitting}
                onEdit={isRegistered ? handleStartEditing : undefined}
                p="0 !important"
                border="0"
                maxW="692px"
                editLabel={
                    <EditIcon position="absolute" top="18px" right={0} w={'20px'} h={'18px'} />
                }
            >
                <ToggleCardEdit>
                    <Container p={0} m={0}>
                        <form onSubmit={form.handleSubmit(submit)}>
                            <Stack spacing={{base: 8, lg: 10}}>
                                {form.formState.errors?.global && (
                                    <Alert status="error">
                                        <AlertIcon color="red.500" boxSize={4} />
                                        <Text fontSize="sm" ml={3}>
                                            {form.formState.errors.global.message}
                                        </Text>
                                    </Alert>
                                )}
                                <ProfileFields form={form} birthDayData={customer?.birthday} />
                                <Button
                                    type="submit"
                                    width={{base: '100%', lg: '343px'}}
                                    height={{base: '44px', lg: '48px'}}
                                    fontSize="md"
                                    padding={{base: '12px 24px', lg: '16px 24px 12px 24px'}}
                                >
                                    <Stack spacing={0} mb="5px">
                                        <FormattedMessage
                                            defaultMessage="Save Changes"
                                            id="form_action_buttons.button.save.changes"
                                        />
                                    </Stack>
                                </Button>
                            </Stack>
                        </form>
                    </Container>

                    <Button
                        variant="link"
                        fontSize="sm"
                        w={{base: '100%', lg: 'auto'}}
                        mt={{base: 8, lg: 10}}
                        onClick={onOpenCloseAccountModal}
                    >
                        <FormattedMessage
                            defaultMessage="Close Account"
                            id="close_account_modal.link"
                        />
                    </Button>
                </ToggleCardEdit>
                <ToggleCardSummary>
                    <Box data-private>
                        {customer?.firstName && <Text>{customer.firstName}</Text>}
                        {customer?.lastName && <Text>{customer.lastName === DEFAULT_CUSTOMER_NAME ? '' : customer.lastName}</Text>}
                        {customer?.email && <Text>{customer.email}</Text>}

                        {customer?.phoneHome && (
                            <Text>
                                <FormattedMessage
                                    id="profile_details.phone"
                                    defaultMessage="Tel.: {phone}"
                                    values={{
                                        phone: customer.phoneHome
                                    }}
                                />
                            </Text>
                        )}

                        {customer?.c_birthdayFormatted && (
                            <Text>{customer.c_birthdayFormatted}</Text>
                        )}

                        {!shouldHideLoyaltyUI &&
                            customer?.c_b2cloyalty_loyaltyProgramMemberNumber && (
                            <Text>
                                <FormattedMessage
                                    id="barcode.loyalt.id"
                                    defaultMessage="Loyalty ID: {loyaltyId}"
                                    values={{
                                        loyaltyId: customer.c_b2cloyalty_loyaltyProgramMemberNumber
                                    }}
                                />
                            </Text>
                        )}
                    </Box>
                </ToggleCardSummary>
            </ToggleCard>

            <CloseAccountModal
                isOpen={isOpenCloseAccountModal}
                onClose={onCloseCloseAccountModal}
                onOpen={onOpenCloseAccountModal}
            />
        </>
    )
}

const AccountDetail = () => {
    const headingRef = useRef()
    const history = useHistory()
    const isMobile = useBreakpointValue({base: true, md: false})

    useEffect(() => {
        // Focus the 'Account Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    return (
        <Stack data-testid="account-detail-page" spacing={6}>
            <Heading
                display="flex"
                alignItems="center"
                as="h1"
                fontSize={{base: '20px', lg: '24px'}}
                tabIndex="0"
                ref={headingRef}
                gap={6}
                textTransform="uppercase"
            >
                {isMobile && (
                    <Button m="0" variant="link" onClick={() => history.push('/account')}>
                        <LeftArrow />
                    </Button>
                )}
                <FormattedMessage
                    defaultMessage="Personal Information"
                    id="account_detail.title.personal_information"
                />
            </Heading>

            <ProfileCard />
        </Stack>
    )
}

AccountDetail.getTemplateName = () => 'account-detail'

export default AccountDetail
